/**
 * HAREKET EKLERİ — para hareketine bağlı dosyalar (banka dekontu, makbuz; görsel ya da PDF).
 *
 * Kullanıcı isteği (2026-09-28): "Ödemelerde de dosya kaydedebilelim." Ataş
 * simgesi, dekont simgesinin durduğu HER listede yanında durur (Kasa/Banka
 * Hareketleri, kasa hesabı ekstresi, cari ekstresi); yeni hareket penceresinde
 * de kayıtla birlikte dosya seçilebilir.
 *
 * Dosyalar JWT'li istemciyle BLOB olarak alınır — adresi doğrudan yeni sekmede
 * açmak kimlik başlığını taşımaz ve 401 döner (bkz. lib/dekont.js).
 * Sunucu tarafı: backend/odeme_ekleri.py.
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Paperclip, FileText, Image as ImageIcon, Download, Trash2, Upload, Loader2, ExternalLink } from 'lucide-react';

export const EK_KABUL = 'image/*,application/pdf';

const boyutYaz = (b) => (b >= 1048576
  ? `${(b / 1048576).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB`
  : `${Math.max(1, Math.round(b / 1024)).toLocaleString('tr-TR')} KB`);

const hataMesaji = async (err, varsayilan) => {
  const veri = err?.response?.data;
  try {
    // Blob istekte hata gövdesi de blob gelir
    if (veri instanceof Blob) return JSON.parse(await veri.text()).detail || varsayilan;
  } catch { /* yoksay */ }
  return veri?.detail || varsayilan;
};

/** Dosyaları bir harekete yükler. Başarılıysa yüklenen kayıt sayısını döner. */
export async function ekleriYukle(hareketId, dosyalar) {
  const form = new FormData();
  Array.from(dosyalar).forEach(d => form.append('dosyalar', d));
  const r = await api.post(`/treasury/transactions/${hareketId}/ekler`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } });
  return (r.data || []).length;
}

async function ekiAc(hareketId, ek, indir) {
  try {
    const r = await api.get(`/treasury/transactions/${hareketId}/ekler/${ek.id}`,
      { params: { indir }, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: ek.content_type }));
    if (indir) {
      const a = document.createElement('a');
      a.href = url;
      a.download = ek.dosya_adi || 'dosya';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else if (!window.open(url, '_blank')) {
      toast.info('Açılır pencere engellendi — dosyayı indirebilirsiniz');
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error(await hataMesaji(err, 'Dosya açılamadı'));
  }
}

/**
 * Ataş düğmesi + ekler penceresi. `sayi` listeden gelir (`ek_sayisi`); ek
 * varsa simge renkli ve yanında sayı yazar, böylece "bu ödemenin dekontu
 * yüklendi mi" sorusu pencere açılmadan cevaplanır.
 */
export default function EkDugmesi({ hareketId, sayi = 0, onDegisti, boyut = 'sm' }) {
  const [acik, setAcik] = useState(false);
  const kutu = boyut === 'sm' ? 'w-7 h-7' : 'w-6 h-6';
  return (
    <>
      <button type="button" onClick={() => setAcik(true)}
        title={sayi ? `Ekler (${sayi})` : 'Dosya ekle (dekont, makbuz)'}
        aria-label="Hareket ekleri"
        className={`relative inline-flex items-center justify-center ${kutu} rounded-md transition-colors
          ${sayi ? 'text-[#2e6fa0] hover:bg-[#2e6fa0]/10' : 'text-[#8a7e70] hover:text-[#2e6fa0] hover:bg-[#2e6fa0]/10'}`}
        data-testid={`ek-dugmesi-${hareketId}`}>
        <Paperclip size={12} strokeWidth={sayi ? 2.4 : 2} />
        {sayi > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[13px] h-[13px] px-[3px] rounded-full bg-[#2e6fa0] text-white text-[8px] font-bold leading-[13px] text-center">
            {sayi}
          </span>
        )}
      </button>
      {acik && (
        <EklerPenceresi hareketId={hareketId} onClose={() => setAcik(false)} onDegisti={onDegisti} />
      )}
    </>
  );
}

function EklerPenceresi({ hareketId, onClose, onDegisti }) {
  const { hasPermission } = useAuth();
  // Sunucu kapısı: yüklemek treasury/edit, silmek treasury/delete
  const yukleyebilir = hasPermission('treasury', 'edit');
  const silebilir = hasPermission('treasury', 'delete');
  const [ekler, setEkler] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [siliniyor, setSiliniyor] = useState(null);
  const girdi = useRef(null);

  const getir = () => api.get(`/treasury/transactions/${hareketId}/ekler`)
    .then(r => setEkler(r.data))
    .catch(async (err) => { setEkler([]); toast.error(await hataMesaji(err, 'Ekler yüklenemedi')); });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getir(); }, [hareketId]);

  const yukle = async (dosyalar) => {
    if (!dosyalar?.length) return;
    setYukleniyor(true);
    try {
      const n = await ekleriYukle(hareketId, dosyalar);
      toast.success(`${n} dosya eklendi`);
      await getir();
      onDegisti?.();
    } catch (err) {
      toast.error(await hataMesaji(err, 'Dosya yüklenemedi'), { duration: 7000 });
    }
    setYukleniyor(false);
    if (girdi.current) girdi.current.value = '';
  };

  const sil = async (ek) => {
    if (!window.confirm(`"${ek.dosya_adi}" silinecek. Emin misiniz?`)) return;
    setSiliniyor(ek.id);
    try {
      await api.delete(`/treasury/transactions/${hareketId}/ekler/${ek.id}`);
      toast.success('Dosya silindi');
      await getir();
      onDegisti?.();
    } catch (err) {
      toast.error(await hataMesaji(err, 'Silinemedi'));
    }
    setSiliniyor(null);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
            <Paperclip size={16} /> Hareket Ekleri
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8a7e70]">
            Banka dekontu, makbuz gibi belgeler — görsel ya da PDF, dosya başına en fazla 15 MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {ekler === null ? (
            <div className="py-6 text-center text-[#8a7e70]"><Loader2 size={16} className="animate-spin inline" /></div>
          ) : ekler.length === 0 ? (
            <p className="py-6 text-center text-sm text-[#8a7e70]">Bu harekete henüz dosya eklenmemiş.</p>
          ) : ekler.map(ek => (
            <div key={ek.id} className="flex items-center gap-2 bg-white border border-[#e8dfd0] rounded-lg px-3 py-2"
              data-testid={`ek-satir-${ek.id}`}>
              {ek.content_type === 'application/pdf'
                ? <FileText size={18} className="text-[#7a3535] shrink-0" />
                : <ImageIcon size={18} className="text-[#2e6fa0] shrink-0" />}
              <button type="button" onClick={() => ekiAc(hareketId, ek, false)}
                className="flex-1 min-w-0 text-left" title="Görüntüle">
                <p className="text-sm font-medium text-[#1a1916] truncate hover:underline">{ek.dosya_adi}</p>
                <p className="text-[10px] text-[#8a7e70]">
                  {boyutYaz(ek.boyut || 0)} · {new Date(ek.yuklendi_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}
                  {ek.yukleyen_adi ? ` · ${ek.yukleyen_adi}` : ''}
                </p>
              </button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a7e70] hover:text-[#1a1916]"
                title="Yeni sekmede aç" onClick={() => ekiAc(hareketId, ek, false)}>
                <ExternalLink size={13} />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a7e70] hover:text-[#1a1916]"
                title="İndir" onClick={() => ekiAc(hareketId, ek, true)}>
                <Download size={13} />
              </Button>
              {silebilir && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a7e70] hover:text-[#7a3535]"
                  title="Sil" disabled={siliniyor === ek.id} onClick={() => sil(ek)}
                  data-testid={`ek-sil-${ek.id}`}>
                  <Trash2 size={13} />
                </Button>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {yukleyebilir ? (
            <>
              <input ref={girdi} type="file" accept={EK_KABUL} multiple className="hidden"
                onChange={e => yukle(e.target.files)} data-testid="ek-dosya-girdi" />
              <Button variant="outline" className="border-[#d0c4b0]" disabled={yukleniyor}
                onClick={() => girdi.current?.click()} data-testid="ek-yukle-btn">
                {yukleniyor ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                Dosya Ekle
              </Button>
            </>
          ) : <span />}
          <Button onClick={onClose} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Yeni hareket formlarındaki dosya seçici. Dosyalar hareket KAYDEDİLDİKTEN
 * sonra yüklenir (`kayittanSonraYukle`) — hareketin kimliği ancak o zaman var.
 */
export function EkSecici({ dosyalar, setDosyalar }) {
  const girdi = useRef(null);
  const ekle = (liste) => {
    if (!liste?.length) return;
    // KOPYA ŞİMDİ alınır: `input.files` CANLI bir listedir ve aşağıda kutu
    // sıfırlanınca boşalır; durum güncelleyicisi sonra çalıştığı için
    // `Array.from` orada çağrılsaydı seçilen dosyalar sessizce kaybolurdu.
    const yeni = Array.from(liste);
    setDosyalar(onceki => [...onceki, ...yeni]);
    if (girdi.current) girdi.current.value = '';
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <input ref={girdi} type="file" accept={EK_KABUL} multiple className="hidden"
          onChange={e => ekle(e.target.files)} data-testid="yeni-hareket-ek-girdi" />
        <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] border-[#d0c4b0]"
          onClick={() => girdi.current?.click()} data-testid="yeni-hareket-ek-btn">
          <Paperclip size={12} className="mr-1.5" /> Dekont / Belge Ekle
        </Button>
        <span className="text-[10px] text-[#8a7e70]">Görsel ya da PDF (isteğe bağlı)</span>
      </div>
      {dosyalar.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dosyalar.map((d, i) => (
            <span key={`${d.name}-${i}`} className="inline-flex items-center gap-1 text-[10px] bg-[#2e6fa0]/10 text-[#2e6fa0] rounded px-1.5 py-0.5 max-w-[220px]">
              <span className="truncate">{d.name}</span>
              <button type="button" className="hover:text-[#7a3535]" aria-label={`${d.name} kaldır`}
                onClick={() => setDosyalar(onceki => onceki.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Kaydedilen hareket(ler)e seçilen dosyaları yükler. Döviz işlemi İKİ bacak
 * yazdığı için belge iki bacağa da eklenir: kullanıcı hangi hesabın
 * ekstresinden bakarsa baksın dekontu bulabilmeli.
 * Yükleme başarısız olursa hareket GERİ ALINMAZ (para hareketi gerçekleşmiş);
 * kullanıcıya ataş simgesinden tekrar ekleyebileceği söylenir.
 */
export async function kayittanSonraYukle(hareketIdleri, dosyalar) {
  if (!dosyalar?.length || !hareketIdleri?.length) return;
  for (const id of hareketIdleri) {
    try {
      await ekleriYukle(id, dosyalar);
    } catch (err) {
      toast.error(`Hareket kaydedildi ama dosya yüklenemedi: ${await hataMesaji(err, 'bilinmeyen hata')}. ` +
        'Listedeki ataş simgesinden tekrar ekleyebilirsiniz.', { duration: 10000 });
      return;
    }
  }
  toast.success(`${dosyalar.length} dosya harekete eklendi`);
}
